import React, { useState, useEffect, useCallback, useContext } from "react";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import { AuthContext } from "../../contexts/Auth/AuthContext";
import { EmpresaContext } from "../../contexts/Empresa/EmpresaContext";
import { FiltrosData } from "../../components/FiltrosData/FiltrosData";
import { DadosDashboard } from "../../types/Metricas";
import { api } from "../../hooks/useApi";
import { PERMISSOES } from "../../services/Consts";
import moment from "moment";
import "moment/locale/pt-br";
import { ReactComponent as DashVisitasSVG } from "../../assets/newImages/icons/DashVisitas.svg";
import { ReactComponent as DashVisitasCatalogoSVG } from "../../assets/newImages/icons/DashVisitasCatalogos.svg";
import { ReactComponent as DashSacolasSVG } from "../../assets/newImages/icons/DashSacolas.svg";
import { ReactComponent as DashPedidosSVG } from "../../assets/newImages/icons/DashPedidos.svg";
import { ReactComponent as DashTicketSVG } from "../../assets/newImages/icons/DashTicket.svg";
import { ReactComponent as DashProdutosSVG } from "../../assets/newImages/icons/DashProdutos.svg";
import imagemProduto from "../../assets/newImages/home/image_produto.png";
import Chart from 'react-apexcharts';
import { TagVariacao } from "../../components/TagVariacao/TagVariacao";
import { carregarMetricas } from "../../utils/metricasLoader";
import {
  formatarData,
  getLineChartConfig,
  getBarChartConfig,
  getHorizontalBarChartConfig
} from "../../utils/chartConfig";
import {
  GraficosMetricas,
  TituloCard,
  QuantidadesCard,
  LinhaCards,
  Card,
  QuantidadesCardTicket,
  ListagemPorSituacao,
  QuantidadesCardSacola
} from "./styles";
import { Select } from "../../components/Select";
import SkeletonCardMetrica from "../../components/Skeletons/SkeletonCardMetrica/SkeletonCardMetrica";

interface MetricasProps { }

interface FiltroDataProps {
  inicio: string;
  fim: string;
  placeholder: string;
}

const coresPedidos: Record<string, string> = {
  1: "#FFBE9D",  // Em aberto
  7: "#E7BE00",  // Em análise
  8: "#4DE889",  // Pago
  4: "#00A2BB",  // Em produção
  5: "#0093FB",  // Enviado
  2: "#0D5229",  // Finalizado
  3: "#9BA9FB",  // Cancelado
};

const optionsTipoValor = [
  { name: "Valor total", value: "total" },
  { name: "Valor sem frete", value: "sem_frete" },
];

const coresSacolas: Record<number, string> = {
  1: "#4DE889",  // Em aberto
  2: "#E7BE00",  // Em contato
  3: "#FFBE9D",  // Não convertido
};

export const Metricas: React.FC<MetricasProps> = () => {
  const { theme } = useTheme();
  const { permissoesUsuario, customizadas } = useContext(AuthContext);
  const { empresa } = useContext(EmpresaContext);

  // Vê todos os usuários nas métricas: customizado por METRICAS_GERAL; não-customizado pelo legado (pedido-gerente).
  const podeVerTodasMetricas = customizadas
    ? permissoesUsuario.includes(PERMISSOES.METRICAS_GERAL)
    : permissoesUsuario.includes(PERMISSOES.VISUALIZAR_PEDIDOS_GERENTE);

  const emailUsuarioLogado = empresa?.email_notificacao || "";

  const isDark = theme.mode === 'dark';
  const chartColors = {
    primary: theme.colors.primary,
      secondary: theme.colors.secondary,
      text: theme.colors.tertiary,
    grid: isDark ? '#444' : '#e0e0e0',
  };

  const [optionsUsuarios, setOptionsUsuarios] = useState<
    { name: string; value: string }[]
  >([]);
  // Se não for gerente, já inicia com o email do próprio usuário
  const [filtroUsuarioAux, setFiltroUsuarioAux] = useState(podeVerTodasMetricas ? "todos" : emailUsuarioLogado);
  const [dadosDashboard, setDadosDashboard] = useState<DadosDashboard | null>(null);
  const [tipoValor, setTipoValor] = useState("total");
  const [initialLoad, setInitialLoad] = useState(true);
  const [loadingResumo, setLoadingResumo] = useState(true);
  const [loadingVisitantes, setLoadingVisitantes] = useState(true);
  const [loadingPedidos, setLoadingPedidos] = useState(true);
  const [loadingCupom, setLoadingCupom] = useState(true);
  const [loadingEstados, setLoadingEstados] = useState(true);

  const [filtroData, setFiltroData] = useState<FiltroDataProps>({
    inicio: `${moment().subtract(30, "days").format("YYYY-MM-DD")} 00:00:00`,
    fim: `${moment().format("YYYY-MM-DD")} 23:59:59`,
    placeholder: "Últimos 30 dias"
  });

  const handleAplicarFiltroData = (newValue: FiltroDataProps) => {
    setFiltroData(newValue);

    if (initialLoad) return;

    buscarDados(newValue.inicio, newValue.fim, filtroUsuarioAux);
  };


  const valorFaturado = tipoValor === "sem_frete" ? dadosDashboard?.valor_faturado_sem_frete : dadosDashboard?.valor_faturado;
  const ticketMedio = tipoValor === "sem_frete" ? dadosDashboard?.ticket_medio_sem_frete : dadosDashboard?.ticket_medio;

  const buscarDados = useCallback(async (dataInicio?: string, dataFim?: string, idUsuario?: string) => {
    setLoadingResumo(true);
    setLoadingVisitantes(true);
    setLoadingPedidos(true);
    setLoadingCupom(true);
    setLoadingEstados(true);

    await carregarMetricas(
      { dataInicio, dataFim, idUsuario },
      {
        onResumoLoaded: (dados) => {
          setDadosDashboard(prev => ({ ...prev, ...dados } as DadosDashboard));
          setLoadingResumo(false);
        },
        onVisitantesLoaded: (dados) => {
          setDadosDashboard(prev => ({ ...prev, ...dados } as DadosDashboard));
          setLoadingVisitantes(false);
        },
        onPedidosLoaded: (dados) => {
          setDadosDashboard(prev => ({ ...prev, ...dados } as DadosDashboard));
          setLoadingPedidos(false);
        },
        onCupomLoaded: (dados) => {
          setDadosDashboard(prev => ({ ...prev, ...dados } as DadosDashboard));
          setLoadingCupom(false);
        },
        onEstadosLoaded: (dados) => {
          setDadosDashboard(prev => ({ ...prev, ...dados } as DadosDashboard));
          setLoadingEstados(false);
        },
        onError: (error, tipo) => {
          console.error(`Erro ao buscar ${tipo}:`, error);
          // Desativar loading específico
          switch(tipo) {
            case 'resumo': setLoadingResumo(false); break;
            case 'visitantes': setLoadingVisitantes(false); break;
            case 'pedidos': setLoadingPedidos(false); break;
            case 'cupom': setLoadingCupom(false); break;
            case 'estados': setLoadingEstados(false); break;
          }
        }
      }
    );
  }, []);

  const buscarUsuarios = async () => {
    try {
      const response = await api.post('/usuario/todos', { escopo: 'metricas' });
      const usuarios = response.data;

      const options = [
        { name: 'Todos os usuários', value: 'todos' },
        ...usuarios
          .filter((u: any) => u.nome) // Filtrar apenas usuários com nome
          .map((u: any) => ({
            name: u.nome,
            value: u.email_notificacao || u.nome // Usar email como ID, ou nome como fallback
          }))
      ];

      setOptionsUsuarios(options);
      setFiltroUsuarioAux('todos');

    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
    }
  };


  useEffect(() => {
    if (!podeVerTodasMetricas && !emailUsuarioLogado) {
      return;
    }

    const filtroInicial = podeVerTodasMetricas ? "todos" : emailUsuarioLogado;
    setFiltroUsuarioAux(filtroInicial);
    buscarDados(filtroData.inicio, filtroData.fim, filtroInicial);

    if (podeVerTodasMetricas) {
      buscarUsuarios();
    }

    setTimeout(() => {
      setInitialLoad(false);
    }, 1000);
  }, [podeVerTodasMetricas, emailUsuarioLogado]);

  return (
    <GraficosMetricas theme={theme}>
      <div className="cabecalhoMetricas">
        <h2 className="tituloMetricas fonteTitulo">
          Análise de dados
          <span className="fonteRotulos">Dados atualizam duas vezes ao dia, não é em tempo real. Última atualização: {dadosDashboard?.atualizado_em ? moment(dadosDashboard.atualizado_em).format('DD/MM/YYYY [às] HH:mm[h]') : ''}</span>
        </h2>

        <div className="filtrosMetricas">
          {podeVerTodasMetricas && (
            <Select
              options={optionsUsuarios}
              value={filtroUsuarioAux}
              placeholder="Todos os usuários"
              onChange={(newValue) => {
                if (initialLoad) {
                  return;
                }
                setFiltroUsuarioAux(newValue);
                buscarDados(filtroData.inicio, filtroData.fim, newValue);
              }}
            ></Select>
          )}
          <Select
            options={optionsTipoValor}
            value={tipoValor}
            placeholder="Valor total"
            onChange={(newValue) => setTipoValor(newValue)}
          ></Select>
          <FiltrosData
            value={filtroData}
            onAplicar={handleAplicarFiltroData}
            hiddenOptions={["Do dia"]}
            maxDate={moment().subtract(1, 'day').format('YYYY-MM-DD')}
          />
        </div>
      </div>

      <div className="conteudoMetricas">
        {/* Linha de Cards de visitas */}
        <LinhaCards theme={theme}>
          {/* Gráfico de visitantes */}
          {loadingVisitantes || loadingResumo ? (
            <SkeletonCardMetrica altura="35rem" />
          ) : (
            <Card theme={theme}>
              <div className="headerCard">
                <TituloCard theme={theme}>
                  <DashVisitasSVG className="iconCard" />
                  <div className="textoTitulo fonteRotulos">
                    <strong className="principal fonteTexto">
                      Visitantes
                    </strong>
                    Visitantes no período, em todos os catálogos
                  </div>
                </TituloCard>
                <QuantidadesCard theme={theme}>
                  <strong className="fonteSubtitulo">{dadosDashboard?.visitas?.total || 0}</strong>
                  <TagVariacao variacao={dadosDashboard?.visitas?.variacao || 0} />
                </QuantidadesCard>
              </div>
              <div className="grafico">
                <Chart
                  type="line"
                  width="100%"
                  height="100%"
                  series={[{
                    name: 'Visitantes',
                    data: dadosDashboard?.historico_visitantes && dadosDashboard.historico_visitantes.length > 0
                      ? dadosDashboard.historico_visitantes.map(item => item.quantidade)
                      : []
                  }]}
                  options={getLineChartConfig(
                    { isDark, chartColors },
                    chartColors.primary,
                    dadosDashboard?.historico_visitantes && dadosDashboard.historico_visitantes.length > 0
                      ? dadosDashboard.historico_visitantes.map(item => formatarData(item.data))
                      : [''],
                    dadosDashboard?.historico_visitantes && dadosDashboard.historico_visitantes.length > 0
                  )}
                />
              </div>
            </Card>
          )}

          {/* Visitantes por catálogo */}
          {loadingResumo ? (
            <SkeletonCardMetrica altura="35rem" />
          ) : (
            <Card theme={theme}>
              <TituloCard theme={theme}>
                <DashVisitasCatalogoSVG className="iconCard" />
                <div className="textoTitulo fonteRotulos">
                  <strong className="principal fonteTexto">
                    Visitantes por catálogo
                  </strong>
                  Catálogos mais acessados
                </div>
              </TituloCard>
              {/* Tabela de catálogos */}
              <table className="tabela">
                <thead className="cabecalhoTabela">
                  <tr>
                    <th className="fonteRotulos">Catálogos</th>
                    <th className="fonteRotulos">Visitantes</th>
                  </tr>
                </thead>
                <tbody className="bodyTabela">
                  {dadosDashboard?.visitas?.por_catalogo && dadosDashboard.visitas.por_catalogo.length > 0 ? (
                    dadosDashboard.visitas.por_catalogo.map((catalogo, index) => (
                      <tr key={index}>
                        <td className="fonteRotulos">{catalogo.catalogo}</td>
                        <td className="fonteRotulos">{catalogo.visitantes}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="fonteRotulos textoCentralizado">
                        Nenhum dado disponível
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card>
          )}
        </LinhaCards>

        {/* Linha de Cards de pedidos */}
        <LinhaCards theme={theme}>
          {/* Listagens */}
          <div className="quantidadesPedidosSacolas">
            <div className="sacolas">
              {/* Quantidades sacolas por status */}
              {loadingResumo ? (
                <SkeletonCardMetrica altura="22rem" />
              ) : (
              <Card theme={theme}>
                <TituloCard theme={theme}>
                  <DashSacolasSVG className="iconCard" />
                  <div className="textoTitulo fonteRotulos">
                    <strong className="principal fonteTexto">
                      Sacolas abandonadas
                    </strong>
                    Classificados por status
                  </div>
                </TituloCard>
                <ListagemPorSituacao theme={theme}>
                  {dadosDashboard?.sacolas_abandonadas?.por_status && dadosDashboard.sacolas_abandonadas.por_status.length > 0 ? (
                    dadosDashboard.sacolas_abandonadas.por_status.map((status, index) => (
                      <div key={index} className="linhaSituacao fonteRotulos">
                        <div className="situacao">
                          <span
                            className="corSituacao"
                            style={{ backgroundColor: coresSacolas[status.id_situacao] || '#ccc' }}
                          ></span>
                          <span className="quantidade">{status.quantidade}</span>
                        </div>
                        <span className="descricao">{status.descricao}</span>
                      </div>
                    ))
                  ) : (
                    <div className="linhaSituacao fonteRotulos">
                      <span className="descricao">Nenhum dado disponível</span>
                    </div>
                  )}
                </ListagemPorSituacao>
              </Card>
              )}

              {/* Estimativa de ganhos sacolas */}
              {loadingResumo ? (
                <SkeletonCardMetrica altura="22rem" />
              ) : (
              <Card theme={theme}>
                <TituloCard theme={theme}>
                  <DashSacolasSVG className="iconCard" />
                  <div className="textoTitulo fonteRotulos">
                    <strong className="principal fonteTexto">
                      Sacolas abandonadas
                    </strong>
                    Estimativa de ganhos
                  </div>
                </TituloCard>
                <QuantidadesCardSacola theme={theme}>
                  <strong className="fonteSubtitulo">
                    R$ {(dadosDashboard?.sacolas_abandonadas?.estimativa_ganhos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </strong>
                  <TagVariacao variacao={dadosDashboard?.sacolas_abandonadas?.variacao_estimativa || 0} />
                </QuantidadesCardSacola>
              </Card>
              )}
            </div>

            {/* Quantidades pedios por status */}
            {loadingResumo ? (
              <SkeletonCardMetrica altura="45rem" />
            ) : (
            <Card theme={theme}>
              <TituloCard theme={theme}>
                <DashPedidosSVG className="iconCard" />
                <div className="textoTitulo fonteRotulos">
                  <strong className="principal fonteTexto">
                    Pedidos em aberto
                  </strong>
                  Classificados por status
                </div>
              </TituloCard>

              <ListagemPorSituacao theme={theme}>
                {dadosDashboard?.pedidos?.por_status && dadosDashboard.pedidos.por_status.length > 0 ? (
                  dadosDashboard.pedidos.por_status.map((status, index) => (
                    <div key={index} className="linhaSituacao fonteRotulos">
                      <div className="situacao">
                        <span
                          className="corSituacao"
                          style={{ backgroundColor: coresPedidos[status.id_situacao] || '#ccc' }}
                        ></span>
                        <span className="quantidade">{status.quantidade}</span>
                      </div>
                      <span className="descricao">{status.descricao}</span>
                    </div>
                  ))
                ) : (
                  <div className="linhaSituacao fonteRotulos">
                    <span className="descricao">Nenhum dado disponível</span>
                  </div>
                )}
              </ListagemPorSituacao>
            </Card>
            )}
          </div>

          {/* Gráficos */}
          {loadingPedidos || loadingResumo ? (
            <SkeletonCardMetrica altura="45rem" />
          ) : (
          <Card theme={theme}>
            <div className="headerCard">
              <TituloCard theme={theme}>
                <DashPedidosSVG className="iconCard" />
                <div className="textoTitulo fonteRotulos">
                  <strong className="principal fonteTexto">
                    Quantidade de pedidos
                  </strong>
                  Todos os pedidos, desconsiderando os cancelados
                </div>
              </TituloCard>
              <QuantidadesCard theme={theme}>
                <strong className="fonteSubtitulo">{dadosDashboard?.pedidos?.total || 0}</strong>
                <TagVariacao variacao={dadosDashboard?.pedidos?.variacao || 0} />
              </QuantidadesCard>
            </div>

            <div className="graficosPedidos">
              <Chart
                type="line"
                width="100%"
                height="100%"
                series={[{
                  name: 'Pedidos',
                  data: dadosDashboard?.historico_pedidos && dadosDashboard.historico_pedidos.length > 0
                    ? dadosDashboard.historico_pedidos.map(item => item.quantidade)
                    : []
                }]}
                options={getLineChartConfig(
                  { isDark, chartColors },
                  chartColors.secondary,
                  dadosDashboard?.historico_pedidos && dadosDashboard.historico_pedidos.length > 0
                    ? dadosDashboard.historico_pedidos.map(item => formatarData(item.data))
                    : [''],
                  dadosDashboard?.historico_pedidos && dadosDashboard.historico_pedidos.length > 0
                )}
              />
            </div>
          </Card>
          )}
        </LinhaCards>

        <LinhaCards theme={theme}>
          <div className="faturamento">
            {loadingResumo ? (
              <SkeletonCardMetrica altura="20rem" />
            ) : (
            <Card theme={theme}>
              <div className="headerCard">
                <TituloCard theme={theme}>
                  <DashTicketSVG className="iconCard" />
                  <div className="textoTitulo fonteRotulos">
                    <strong className="principal fonteTexto">
                      Valor faturado
                    </strong>
                    Valor das vendas, desconsiderando cancelados
                  </div>
                </TituloCard>
                <QuantidadesCardTicket theme={theme}>
                  <strong className="fonteSubtitulo">
                    R$ {Number(valorFaturado?.total ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </strong>
                  <TagVariacao variacao={Number(valorFaturado?.variacao ?? 0)} />
                </QuantidadesCardTicket>
              </div>
            </Card>
            )}

            {/* Card ticket médio */}
            {loadingResumo ? (
              <SkeletonCardMetrica altura="20rem" />
            ) : (
            <Card theme={theme}>
              <div className="headerCard">
                <TituloCard theme={theme}>
                  <DashTicketSVG className="iconCard" />
                  <div className="textoTitulo fonteRotulos">
                    <strong className="principal fonteTexto">
                      Ticket médio
                    </strong>
                    Valor das vendas dividido pela quantidade de pedidos
                  </div>
                </TituloCard>
                <QuantidadesCardTicket theme={theme}>
                  <strong className="fonteSubtitulo">
                    R$ {Number(ticketMedio?.valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </strong>
                  <TagVariacao variacao={Number(ticketMedio?.variacao ?? 0)} />
                </QuantidadesCardTicket>
              </div>

            </Card>
            )}

            {/* Card taxa média de conversão */}
            {loadingResumo ? (
              <SkeletonCardMetrica altura="20rem" />
            ) : (
            <Card theme={theme}>
              <div className="headerCard">
                <TituloCard theme={theme}>
                  <DashTicketSVG className="iconCard" />
                  <div className="textoTitulo fonteRotulos">
                    <strong className="principal fonteTexto">
                      Taxa média de conversão
                    </strong>
                    Proporção de visitantes que resultaram em compras
                  </div>
                </TituloCard>
                <QuantidadesCardTicket theme={theme}>
                  <strong className="fonteSubtitulo">{(dadosDashboard?.taxa_conversao?.percentual || 0).toFixed(2)}%</strong>
                  <TagVariacao variacao={dadosDashboard?.taxa_conversao?.variacao || 0} />
                </QuantidadesCardTicket>
              </div>
            </Card>
            )}
          </div>

          {/* Card produtos mais vendidos */}
          {loadingResumo ? (
            <SkeletonCardMetrica altura="40rem" />
          ) : (
          <Card theme={theme}>
            <TituloCard theme={theme}>
              <DashProdutosSVG className="iconCard" />
              <div className="textoTitulo fonteRotulos">
                <strong className="principal fonteTexto">
                  5 produtos mais vendidos
                </strong>
                Quantidade de vendas por produto
              </div>
            </TituloCard>
            <table className="tabela">
              <thead className="cabecalhoTabela">
                <tr>
                  <th className="fonteRotulos">Mídia</th>
                  <th className="fonteRotulos">SKU</th>
                  <th className="fonteRotulos">Nome do produto</th>
                  <th className="fonteRotulos">Quantidade</th>
                </tr>
              </thead>
              <tbody className="bodyTabela">
                {dadosDashboard?.produtos_mais_vendidos && dadosDashboard.produtos_mais_vendidos.length > 0 ? (
                  dadosDashboard.produtos_mais_vendidos.map((produto, index) => (
                    <tr key={index}>
                      <td className="fonteRotulos">
                        <img
                          src={produto.imagem || imagemProduto}
                          alt={produto.nome_produto}
                          className="imagemProduto"
                          onError={(e) => { e.currentTarget.src = imagemProduto; }}
                        />
                      </td>
                      <td className="fonteRotulos">{produto.referencia || '-'}</td>
                      <td className="fonteRotulos">{produto.nome_produto}</td>
                      <td className="fonteRotulos">{produto.quantidade_vendida}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="fonteRotulos textoCentralizado">
                      Nenhum dado disponível
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
          )}
        </LinhaCards>

        <LinhaCards theme={theme}>
          {loadingCupom || loadingResumo ? (
            <SkeletonCardMetrica altura="35rem" />
          ) : (
          <Card theme={theme}>
            <div className="headerCard">
              <TituloCard theme={theme}>
                <DashVisitasSVG className="iconCard" />
                <div className="textoTitulo fonteRotulos">
                  <strong className="principal fonteTexto">
                    Pedidos com cupom
                  </strong>
                  Pedidos que foram finalizados com cupom de desconto
                </div>
              </TituloCard>
              <QuantidadesCard theme={theme}>
                <strong className="fonteSubtitulo">{dadosDashboard?.pedidos_com_cupom?.total || 0}</strong>
                <TagVariacao variacao={dadosDashboard?.pedidos_com_cupom?.variacao || 0} />
              </QuantidadesCard>
            </div>
            <div className="graficoPedidosCupom">
              <Chart
                type="bar"
                width="100%"
                height="100%"
                series={[
                  {
                    name: 'Com cupom',
                    data: dadosDashboard?.historico_cupom && dadosDashboard.historico_cupom.length > 0
                      ? dadosDashboard.historico_cupom.map(item => item.com_cupom)
                      : []
                  }
                ]}
                options={getBarChartConfig(
                  { isDark, chartColors },
                  chartColors.primary,
                  dadosDashboard?.historico_cupom && dadosDashboard.historico_cupom.length > 0
                    ? dadosDashboard.historico_cupom.map(item => formatarData(item.data))
                    : [''],
                  dadosDashboard?.historico_cupom && dadosDashboard.historico_cupom.length > 0
                )}
              />
            </div>
          </Card>
          )}

          {loadingEstados ? (
            <SkeletonCardMetrica altura="35rem" />
          ) : (
          <Card theme={theme}>
            <TituloCard theme={theme}>
              <DashPedidosSVG className="iconCard" />
              <div className="textoTitulo fonteRotulos">
                <strong className="principal fonteTexto">
                  Pedidos por estado
                </strong>
                Top 5 estados com mais vendas
              </div>
            </TituloCard>
            <div className="graficoPedidosEstado">
              <Chart
                type="bar"
                width="100%"
                height="100%"
                series={[{
                  name: 'Quantidade',
                  data: dadosDashboard?.historico_estados && dadosDashboard.historico_estados.length > 0
                    ? dadosDashboard.historico_estados.map(item => ({
                        x: item.estado,
                        y: item.quantidade
                      }))
                    : []
                }]}
                options={getHorizontalBarChartConfig(
                  { isDark, chartColors },
                  chartColors.primary,
                  dadosDashboard?.historico_estados && dadosDashboard.historico_estados.length > 0
                )}
              />
            </div>
          </Card>
          )}
        </LinhaCards>
      </div>
    </GraficosMetricas>
  );
};